export type GlossaryRow = {
  key: string;
  field: string;
  meaning: string;
};

export type GlossarySection = {
  heading: string;
  title: string;
  intro?: string;
  rows: GlossaryRow[];
};

const CONCEPTS: GlossaryRow[] = [
  {
    key: "company",
    field: "company",
    meaning:
      "A customer organisation in eFreight (tenant). All bookings belong to exactly one company. Partner API calls that read bookings must always pass company_id.",
  },
  {
    key: "user",
    field: "user (portal user)",
    meaning:
      "A login on the eFreight customer portal, linked to one or more companies via user_companies. Not the same as “company”. identity.resolve can match on a user’s email.",
  },
  {
    key: "customer",
    field: "customer",
    meaning:
      "Informal name for the company (or their staff) you are helping in WhatsApp/Gmail. In API fields, prefer company / users — there is no separate “customer” object.",
  },
  {
    key: "partner_key",
    field: "partner API key (effp_…)",
    meaning:
      "eFreight-operated secret for this integration. Cross-tenant read access. Different from company keys (eff_…) that customers use for their own Account API.",
  },
  {
    key: "eff_reference",
    field: "eff_reference / EFF…SEA",
    meaning:
      "Human booking label: EFF + numeric id + transport abbreviation (e.g. SEA, AIR). Often what customers quote in chat. May differ from reference if a custom reference was set.",
  },
  {
    key: "q",
    field: "q",
    meaning:
      "Free-text booking token in requests: full EFF10042SEA, EFF10042, or just 10042. Used in identity.resolve and bookings.lookup.",
  },
  {
    key: "scope_open",
    field: 'scope: "open"',
    meaning:
      "Non-terminal bookings only (submitted through delivery_planned). Excludes delivered, cancelled, rejected, archived, concept, etc.",
  },
];

const MATCH_REASONS: GlossaryRow[] = [
  {
    key: "company_invoice_email",
    field: "company_invoice_email",
    meaning: "Matched company.invoice_email against the email you sent.",
  },
  {
    key: "company_phone",
    field: "company_phone",
    meaning: "Matched company.phone_number against the phone you sent.",
  },
  {
    key: "user_email",
    field: "user_email",
    meaning:
      "Matched a portal user’s email, then loaded that user’s linked companies.",
  },
  {
    key: "booking_reference",
    field: "booking_reference",
    meaning:
      "Matched a booking via q; company comes from that booking’s company_id.",
  },
];

const BOOKING_IDENTITY: GlossaryRow[] = [
  {
    key: "id",
    field: "id",
    meaning: "Internal numeric booking id (also embedded in EFF… references).",
  },
  {
    key: "reference",
    field: "reference",
    meaning:
      "Optional custom reference stored on the booking. If empty, customers usually see eff_reference instead.",
  },
  {
    key: "eff_reference",
    field: "eff_reference",
    meaning: "Canonical display id: EFF{id}{transport abbreviation}.",
  },
  {
    key: "display_reference",
    field: "display_reference",
    meaning: "reference if set, otherwise eff_reference — safest label to show end users.",
  },
  {
    key: "company_id",
    field: "company_id",
    meaning: "Owning company. Always verify this matches the resolved tenant.",
  },
  {
    key: "status",
    field: "status",
    meaning:
      "Lifecycle stage (submitted, in_transit, delivered, …). See API docs bookings.list for the full list.",
  },
  {
    key: "rejection_reason",
    field: "rejection_reason",
    meaning: "Filled when status is rejected — why ops declined the booking.",
  },
  {
    key: "notes",
    field: "notes",
    meaning: "Internal/ops notes on the booking — treat as sensitive.",
  },
];

const DATES_TRANSPORT: GlossaryRow[] = [
  {
    key: "etd",
    field: "container.etd",
    meaning:
      "Estimated Time of Departure (vessel/flight leave origin). Planned, not proof it already sailed.",
  },
  {
    key: "delayed_etd",
    field: "container.delayed_etd",
    meaning:
      "Updated/delayed ETD when the schedule slipped. Prefer over etd when present.",
  },
  {
    key: "effective_etd",
    field: "container.effective_etd",
    meaning: "delayed_etd if set, else etd — best single departure estimate.",
  },
  {
    key: "eta",
    field: "container.eta",
    meaning: "Estimated Time of Arrival at destination port (ocean/air schedule).",
  },
  {
    key: "eta_customer",
    field: "container.eta_customer",
    meaning:
      "ETA communicated toward the customer (may differ from raw schedule eta).",
  },
  {
    key: "customer_eta",
    field: "customer_eta",
    meaning:
      "Best ETA to tell the customer: booking.eta override, else container.eta_customer, else similar. Prefer this over digging nested fields.",
  },
  {
    key: "booking_eta_override",
    field: "booking_eta_override / eta",
    meaning:
      "Optional ETA set on the booking itself (overrides container dates when present).",
  },
  {
    key: "origin_pickup_date",
    field: "origin_pickup_date",
    meaning: "Planned pickup of goods at supplier/origin.",
  },
  {
    key: "confirmed_pick_up_date",
    field: "confirmed_pick_up_date",
    meaning: "Confirmed pickup date once arranged.",
  },
  {
    key: "origin_received_date",
    field: "origin_received_date",
    meaning: "Goods received at origin warehouse/agent.",
  },
  {
    key: "incoterm",
    field: "booking_incoterm",
    meaning:
      "Trade term (FOB, EXW, DDP, …) — who pays/handles freight and risk.",
  },
  {
    key: "service_level",
    field: "service_level",
    meaning: "Service product / speed tier chosen for the booking.",
  },
  {
    key: "transport_type",
    field: "transport_type",
    meaning:
      "Mode (sea/air/…). abbreviation feeds the EFF… suffix (SEA, AIR, …).",
  },
  {
    key: "booking_type",
    field: "booking_type",
    meaning: "Booking category/type label from eFreight configuration.",
  },
];

const PARTIES_CARGO: GlossaryRow[] = [
  {
    key: "supplier",
    field: "supplier",
    meaning:
      "Origin supplier/factory (name, contact, address) — who ships the goods out, not the eFreight customer.",
  },
  {
    key: "origin_port",
    field: "origin_port / destination_port",
    meaning: "Ports of loading / discharge (name + country).",
  },
  {
    key: "products",
    field: "products[]",
    meaning:
      "Line items: name, HS code, EAN, pieces, cartons, CBM, weight, value.",
  },
  {
    key: "product_summary",
    field: "product_summary",
    meaning: "Aggregates across products (counts, totals) for quick answers.",
  },
  {
    key: "hs_code",
    field: "products[].hs_code",
    meaning: "Harmonized System customs classification code.",
  },
  {
    key: "cbm",
    field: "products[].cbm",
    meaning: "Cubic metres — volume of the cargo line.",
  },
];

const CONTAINER_DOCS: GlossaryRow[] = [
  {
    key: "container_ref",
    field: "container.reference",
    meaning: "Physical container number (e.g. ABCD1234567), not the EFF booking id.",
  },
  {
    key: "bl_number",
    field: "container.bl_number",
    meaning: "Bill of Lading number for the ocean shipment.",
  },
  {
    key: "track_and_trace",
    field: "container.track_and_trace",
    meaning: "Carrier tracking URL/reference for the main haul.",
  },
  {
    key: "master_container",
    field: "master_container",
    meaning:
      "Shared master B/L / consolidated container. May include same_company_sibling_bookings only — never other companies’ cargo on the same master.",
  },
  {
    key: "siblings",
    field: "same_company_sibling_bookings",
    meaning:
      "Other bookings of this same company on the same master B/L. Safe to mention; do not invent other shippers.",
  },
  {
    key: "documents",
    field: "documents",
    meaning:
      "Presence flags only (has_commercial_invoice, counts). File bytes/paths are never returned.",
  },
  {
    key: "commercial_invoice",
    field: "has_commercial_invoice",
    meaning: "Whether a commercial invoice was uploaded for customs/ops.",
  },
];

const DELIVERY_LASTMILE: GlossaryRow[] = [
  {
    key: "delivery_type",
    field: "delivery_type",
    meaning:
      '"address" = last-mile to a street address; "storage" = warehouse/storage option without address deliveries.',
  },
  {
    key: "storage_option",
    field: "storage_option",
    meaning: "Chosen storage/warehouse option when not delivering to address.",
  },
  {
    key: "delivery_details",
    field: "delivery_details[]",
    meaning:
      "Delivery address rows (street, postal, city, country, instructions).",
  },
  {
    key: "unloading",
    field: "unloading",
    meaning:
      "Warehouse unload facts: status, unloaded_at, actual cartons/pallets, notes.",
  },
  {
    key: "last_mile",
    field: "last_mile_shipments[]",
    meaning:
      "Sendcloud (or similar) parcel shipments after customs — carrier, tracking_number, events.",
  },
  {
    key: "extra_services",
    field: "extra_services",
    meaning:
      "Optional origin services flags: export_clearance, pick_up_fee, commodity_inspection, post_export_license.",
  },
];

const CUSTOMS_TIMELINE: GlossaryRow[] = [
  {
    key: "lean_customs",
    field: "lean_customs / customs",
    meaning:
      "Dutch customs integration (LeanCustoms). order_id / declaration_id link to the declaration. Live LC payload may be limited in partner-api v1 (ids always present when set).",
  },
  {
    key: "fyco",
    field: "status: fyco",
    meaning:
      "Customs physical check / FYCO stage — goods held for inspection before release.",
  },
  {
    key: "timeline_comments",
    field: "timeline.comments",
    meaning: "Ops/customer-visible comments on the booking thread.",
  },
  {
    key: "timeline_notifications",
    field: "timeline.notifications",
    meaning:
      "System events (often status changes) with change_data — useful for “what happened when”.",
  },
];

const LIST_ROW: GlossaryRow[] = [
  {
    key: "list_eff",
    field: "bookings[].eff_reference",
    meaning: "Compact list label for chat menus.",
  },
  {
    key: "list_eta",
    field: "bookings[].eta",
    meaning: "Best available ETA for the list row (booking or container).",
  },
  {
    key: "list_container",
    field: "bookings[].container_reference",
    meaning: "Container number on the list row, if assigned.",
  },
];

export const GLOSSARY_SECTIONS: GlossarySection[] = [
  {
    heading: "Core concepts",
    title: "Who / what",
    rows: CONCEPTS,
  },
  {
    heading: "identity.resolve",
    title: "match_reasons",
    intro: "Each company in the resolve response lists why it matched.",
    rows: MATCH_REASONS,
  },
  {
    heading: "Full booking JSON",
    title: "Identity & status",
    intro:
      "Returned by bookings.get and bookings.lookup (enriched). Nested structure mirrors the customer booking view in the dashboard.",
    rows: BOOKING_IDENTITY,
  },
  {
    heading: "Full booking JSON",
    title: "Dates, ports & transport",
    rows: DATES_TRANSPORT,
  },
  {
    heading: "Full booking JSON",
    title: "Parties & cargo",
    rows: PARTIES_CARGO,
  },
  {
    heading: "Full booking JSON",
    title: "Container, master B/L & documents",
    rows: CONTAINER_DOCS,
  },
  {
    heading: "Full booking JSON",
    title: "Delivery & last mile",
    rows: DELIVERY_LASTMILE,
  },
  {
    heading: "Full booking JSON",
    title: "Customs & timeline",
    rows: CUSTOMS_TIMELINE,
  },
  {
    heading: "bookings.list rows",
    title: "Compact list fields",
    intro:
      "List responses are slim — call bookings.get after the user picks a reference.",
    rows: LIST_ROW,
  },
];

function sectionToMarkdown(section: GlossarySection): string {
  const lines = [`### ${section.title}`, ""];
  if (section.intro) {
    lines.push(section.intro, "");
  }
  lines.push("| Field | Meaning |", "| --- | --- |");
  for (const row of section.rows) {
    const field = row.field.replace(/\|/g, "\\|");
    const meaning = row.meaning.replace(/\|/g, "\\|");
    lines.push(`| \`${field}\` | ${meaning} |`);
  }
  lines.push("");
  return lines.join("\n");
}

export function buildGlossaryMarkdown(): string {
  const parts = [
    "# eFreight Partner API — Glossary",
    "",
    "Plain-language meanings for fields that often confuse integrators — especially the full booking JSON from `bookings.get` / `bookings.lookup`.",
    "",
    "> **Dates vs reality:** ETD/ETA values are estimates. Compare them to “today” (Europe/Amsterdam) before saying something already happened. Prefer `customer_eta` and `effective_etd` when answering end users.",
    "",
  ];

  let lastHeading = "";
  for (const section of GLOSSARY_SECTIONS) {
    if (section.heading !== lastHeading) {
      parts.push(`## ${section.heading}`, "");
      lastHeading = section.heading;
    }
    parts.push(sectionToMarkdown(section));
  }

  return parts.join("\n").trim() + "\n";
}

